import { Box, Typography, Tooltip } from '@mui/material';
import { TagType } from 'src/features/sites/types/types';
import { LabelingTypeEnum } from 'src/types';
import { UserDefinedValue } from 'src/types/controllers/controller';
import { convertSecondsToHours } from 'src/utilities';
import featureFlags from '../../../../../feature-flags.json';
import { useAuthentication } from 'src/features/authentication/context';

type Props = {
  label: string;
  liveDataComplete: any;
  id: string;
  restTags: TagType[];
};

export const LabelAndValue = ({ label, liveDataComplete, id, restTags }: Props) => {
  const liveDataTag = liveDataComplete?.tags?.find((item: TagType) => item.id === id);
  const restTag = restTags?.find((tag) => tag.id === id);

  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
  } = useAuthentication();

  const userDefinedValues: UserDefinedValue[] =
    restTag?.advancedTagMapping?.userDefinedValues ?? [];
  const labellingType: LabelingTypeEnum = liveDataTag?.labbelingType || restTag?.labellingType;
  const fallbackValue: string = liveDataTag?.fallbackValue || restTag?.fallbackValue;

  let tagValue: string | number = Number(liveDataTag?.value || restTag?.value);

  //Is only "daily runtime"
  const isOnlyDailyRuntime =
    (restTag?.connectedToAssets ?? []).length > 0 &&
    (restTag?.connectedToAssets ?? []).every(
      (asset) => asset.assignedTagLabel.toLowerCase() === 'daily runtime'
    );

  if (isOnlyDailyRuntime) {
    tagValue = Number(convertSecondsToHours(tagValue, 2).slice(0, -1));
  }

  //labelyingType
  const findUserDefinedItem = userDefinedValues?.find((item) => Number(item.value) === tagValue);

  if (labellingType === LabelingTypeEnum.LABEL) {
    if (!findUserDefinedItem) {
      tagValue = fallbackValue;
    } else {
      const name = findUserDefinedItem?.name;
      tagValue = name ?? fallbackValue;
    }
  }
  if (labellingType === LabelingTypeEnum.VALUE_LABEL) {
    if (!findUserDefinedItem) {
      tagValue = fallbackValue;
    } else {
      const name = findUserDefinedItem?.name;
      const value = findUserDefinedItem?.value;
      tagValue = `${value}-${name}`;
    }
  }

  const isCraneFjCustomer = featureFlags.craneFJNugentBetaCustomerId === customerId;
  const isStAugustineSite = featureFlags.stAugustineFromCraneSiteId === siteId;

  let displayName = label;

  if (isCraneFjCustomer && isStAugustineSite) {
    if (label === 'Total Runtime (s)') {
      displayName = 'Total Runtime (h)';
      tagValue = Number((Number(tagValue) / 3600).toFixed(2));
    } else if (label === 'Average Runtime (s)') {
      displayName = 'Average Runtime (m)';
      tagValue = Number((Number(tagValue) / 60).toFixed(2));
    } else if (label === 'Previous Runtime (s)') {
      displayName = 'Previous Runtime (m)';
      tagValue = Number((Number(tagValue) / 60).toFixed(2));
    } else if (label === 'Average Current (A)') {
      tagValue = Number(tagValue).toFixed(2);
    }
  }

  const craneFjStAugustine = isCraneFjCustomer && isStAugustineSite;
  const MAX_VISIBLE_CHARS = String(tagValue)?.length > 3 ? (craneFjStAugustine ? 20 : 18) : 20;
  const MIN_CHARS_FOR_ELLIPSIS = String(tagValue)?.length > 3 ? (craneFjStAugustine ? 22 : 20) : 22;

  const getDisplayLabel = (label: string) => {
    if (label.length >= MIN_CHARS_FOR_ELLIPSIS) {
      return `${label.slice(0, MAX_VISIBLE_CHARS)}...`;
    }

    return label;
  };

  const displayLabel = getDisplayLabel(displayName);

  return (
    <Box sx={{ display: 'flex' }}>
      <Tooltip title={`${displayName}: ${tagValue}`} placement="top-end">
        <Typography sx={{ fontSize: '13px' }} data-sm={`${restTag?.id}-${tagValue}`}>
          {displayLabel ? `${displayLabel}: ${tagValue}` : tagValue}
        </Typography>
      </Tooltip>
    </Box>
  );
};
